import { t } from "@travily/i18n";
import { MoneyField } from "@ui/components/MoneyField";
import { Wizard } from "@ui/components/Wizard";
import type { ReactNode } from "react";
import { FeeTransparency } from "@/components/FeeTransparency/FeeTransparency";

const COLOR = "green" as const;

export interface ServicePriceAmountStepViewProps {
  /** Current stage index for the wizard progress indicator. */
  currentStage: number;
  /** Total number of stages in the wizard. */
  totalStages: number;
  /** ISO currency code the price is entered and displayed in (the seller currency). */
  currency: string;
  /** Platform commission rate (0–1) shown in the take-home ledger. */
  platformFeeRate: number;
  /** Current price as a major-unit amount string (empty when unset). */
  value: string;
  /** Price in minor units for the fee ledger, or `null` until a positive amount is entered. */
  priceCents: number | null;
  /** Field-level validation error to surface beneath the money input. */
  error?: string;
  /** Whether the primary (continue) action is in flight. */
  primaryPending: boolean;
  /** Tertiary top-bar actions (e.g. "Questions?" / "Finish later"). */
  topBarActions?: ReactNode;
  /** Navigate to the previous stage. Omitted on the first stage. */
  onBack?: () => void;
  /** Submit the current stage and advance. */
  onPrimary: () => void;
  /** Called when the seller edits the price amount. */
  onChange: (value: string) => void;
}

export function ServicePriceAmountStepView({
  currentStage,
  totalStages,
  currency,
  platformFeeRate,
  value,
  priceCents,
  error,
  primaryPending,
  topBarActions,
  onBack,
  onPrimary,
  onChange,
}: ServicePriceAmountStepViewProps) {
  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        heading={t("web.services.creator.steps.price_amount.show.title")}
        helper={t("web.services.creator.steps.price_amount.show.subtitle")}
      >
        <div className="w-full max-w-[600px] text-left">
          <MoneyField
            color={COLOR}
            currency={currency}
            errorMessage={error}
            isInvalid={Boolean(error)}
            isLabelHidden
            label={t("web.services.creator.steps.price_amount.show.price_label")}
            onChange={onChange}
            placeholder={t("web.services.creator.steps.price_amount.show.price_placeholder")}
            value={value}
          />
          <FeeTransparency currency={currency} platformFeeRate={platformFeeRate} priceCents={priceCents} />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
